import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Tabs, Tab, Alert, InputGroup, Badge } from 'react-bootstrap';
import { Save, Bell, Cpu } from 'react-bootstrap-icons';
import { getSystemSettings, updateSystemSettings, testDiscordWebhook } from '../services/settings';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ show: false, variant: 'success', message: '' });
  const [settings, setSettings] = useState({
    // General Settings
    auto_start: true,
    auto_start_mode: 'work',
    max_concurrent_tasks: 5,
    default_cooldown: 60,
    request_timeout: 30,
    
    // Notification Settings
    email_notifications: true,
    discord_webhook: '',
    telegram_bot_token: '',
    telegram_chat_id: '',
    notification_level: 'important',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await getSystemSettings();
      setSettings(prev => ({ ...prev, ...data }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      showStatus('Failed to load settings', 'danger');
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: typeof prev[field] === 'boolean' ? value === 'true' : value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSystemSettings(settings);
      showStatus('Settings saved successfully!', 'success');
    } catch (error) {
      console.error('Failed to save settings:', error);
      showStatus('Failed to save settings', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleTestWebhook = async () => {
    setTestingWebhook(true);
    try {
      await testDiscordWebhook(settings.discord_webhook);
      showStatus('Webhook test successful!', 'success');
    } catch (error) {
      console.error('Webhook test failed:', error);
      showStatus('Webhook test failed: ' + error.message, 'danger');
    } finally {
      setTestingWebhook(false);
    }
  };

  const showStatus = (message, variant) => {
    setSaveStatus({ show: true, variant, message });
    setTimeout(() => setSaveStatus({ ...saveStatus, show: false }), 5000);
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h2 fw-bold mb-1">System Settings</h1>
          <p className="text-muted">Configure application behavior and preferences</p>
        </div>
        <Button 
          variant="primary" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Saving...
            </>
          ) : (
            <>
              <Save size={18} className="me-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {saveStatus.show && (
        <Alert variant={saveStatus.variant} className="mb-4">
          {saveStatus.message}
        </Alert>
      )}

      {/* Settings Tabs */}
      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="px-3 pt-3"
            fill
          >
            {/* General Tab */}
            <Tab eventKey="general" title={
              <span className="d-flex align-items-center">
                <Cpu size={18} className="me-2" />
                General
              </span>
            }>
              <div className="p-4">
                <Row className="g-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Auto Start System</Form.Label>
                      <Form.Select
                        value={settings.auto_start}
                        onChange={(e) => handleInputChange('auto_start', e.target.value)}
                      >
                        <option value={true}>Enabled</option>
                        <option value={false}>Disabled</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Automatically start the reaction system on application launch
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Auto Start Mode</Form.Label>
                      <Form.Select
                        value={settings.auto_start_mode}
                        onChange={(e) => handleInputChange('auto_start_mode', e.target.value)}
                      >
                        <option value="work">Work Mode</option>
                        <option value="dry_simulation">Dry Simulation</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Work Mode: Actual reactions | Dry Simulation: Simulate without sending
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Max Concurrent Tasks</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="20"
                        value={settings.max_concurrent_tasks}
                        onChange={(e) => handleInputChange('max_concurrent_tasks', e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        Maximum number of simultaneous reaction tasks
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Default Cooldown (seconds)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="300"
                        value={settings.default_cooldown}
                        onChange={(e) => handleInputChange('default_cooldown', e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        Default delay between reaction attempts
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Request Timeout (seconds)</Form.Label>
                      <Form.Control
                        type="number"
                        min="5"
                        max="120"
                        value={settings.request_timeout}
                        onChange={(e) => handleInputChange('request_timeout', e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        Timeout for Discord API requests
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Tab>

            {/* Notifications Tab */}
            <Tab eventKey="notifications" title={
              <span className="d-flex align-items-center">
                <Bell size={18} className="me-2" />
                Notifications
              </span>
            }>
              <div className="p-4">
                <Row className="g-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Email Notifications</Form.Label>
                      <Form.Select
                        value={settings.email_notifications}
                        onChange={(e) => handleInputChange('email_notifications', e.target.value)}
                      >
                        <option value={true}>Enabled</option>
                        <option value={false}>Disabled</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Receive email alerts for important events
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Notification Level</Form.Label>
                      <Form.Select
                        value={settings.notification_level}
                        onChange={(e) => handleInputChange('notification_level', e.target.value)}
                      >
                        <option value="all">All Events</option>
                        <option value="important">Important Only</option>
                        <option value="critical">Critical Only</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Minimum severity level for notifications
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-medium">
                        Discord Webhook URL
                        <Badge bg="info" className="ms-2" onClick={handleTestWebhook} style={{ cursor: 'pointer' }}>
                          Test
                        </Badge>
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="url"
                          placeholder="https://discord.com/api/webhooks/..."
                          value={settings.discord_webhook}
                          onChange={(e) => handleInputChange('discord_webhook', e.target.value)}
                        />
                        <Button 
                          variant="outline-secondary" 
                          onClick={handleTestWebhook}
                          disabled={testingWebhook || !settings.discord_webhook}
                        >
                          {testingWebhook ? 'Testing...' : 'Test'}
                        </Button>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Webhook for Discord notifications
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Telegram Bot Token</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Bot token"
                        value={settings.telegram_bot_token}
                        onChange={(e) => handleInputChange('telegram_bot_token', e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium">Telegram Chat ID</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Chat ID"
                        value={settings.telegram_chat_id}
                        onChange={(e) => handleInputChange('telegram_chat_id', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Settings;